import { BackPattern } from '../entities/Drawable/BackPattern';
import { CustomGraphicElement } from '../entities/Drawable/CustomGraphicElement';
import { Graduation } from '../entities/Drawable/Graduation';
import { Image } from '../entities/Drawable/Image';
import { ImageSVG } from '../entities/Drawable/ImageSVG';
import { Text } from '../entities/Drawable/Text';
import { Theme } from '../entities/Drawable/Theme';

type HistoricState = {
    content: any[];
    timestamp: number;
};

export const ENGINE_HISTORIC_CHANGED_EVENT = 'engine-historic-changed';

export class Historic {
    private engine: any;
    private history: HistoricState[] = [];
    private currentIndex: number = -1;
    private readonly maxStates: number = 50;

    constructor(engine: any) {
        this.engine = engine;
    }

    private notifyHistoryChanged(): void {
        if (typeof window === 'undefined') {
            return;
        }
        window.dispatchEvent(new CustomEvent(ENGINE_HISTORIC_CHANGED_EVENT));
    }

    get index(): number {
        return this.currentIndex;
    }
    set index(value: number) {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return;
        }
        if (this.history.length === 0) {
            this.currentIndex = -1;
            this.notifyHistoryChanged();
            return;
        }
        const clamped = Math.max(-1, Math.min(value, this.history.length - 1));
        this.currentIndex = clamped;
        this.notifyHistoryChanged();
    }

    save(): void {
        const state: HistoricState = {
            content: this.serializeContent(this.engine.content || []),
            timestamp: Date.now()
        };

        this.history = this.history.slice(0, this.currentIndex + 1);

        this.history.push(state);
        this.currentIndex++;

        if (this.history.length > this.maxStates) {
            this.history.shift();
            this.currentIndex--;
        }
        this.notifyHistoryChanged();
    }

    undo(): void {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            const state = this.history[this.currentIndex];
            this.engine.content = this.hydrateContent(state?.content || []);
            this.notifyHistoryChanged();
        }
    }

    redo(): void {
        if (this.currentIndex < this.history.length - 1) {
            this.currentIndex++;
            const state = this.history[this.currentIndex];
            this.engine.content = this.hydrateContent(state?.content || []);
            this.notifyHistoryChanged();
        }
    }

    canUndo(): boolean {
        return this.currentIndex > 0;
    }

    canRedo(): boolean {
        return this.currentIndex < this.history.length - 1;
    }

    getCurrentState(): any[] {
        if (this.currentIndex < 0) {
            return [];
        }
        const state = this.history[this.currentIndex];
        return this.hydrateContent(state?.content || []);
    }

    getCurrentSave(): any[] {
        try {
            const canvas = this.engine?.canvas?.canvas;
            if (!canvas?.toJSON) {
                return [];
            }
            const json = canvas.toJSON();
            if (json && typeof (json as any).then === 'function') {
                return [];
            }
            return Array.isArray((json as any)?.objects) ? (json as any).objects : [];
        } catch {
            return [];
        }
    }

    clear(): void {
        this.history = [];
        this.currentIndex = -1;
        this.notifyHistoryChanged();
    }

    /**
     * Public method to hydrate serialized content (for loading saved configurations)
     */
    public hydrateSerializedContent(serialized: any[]): any[] {
        return this.hydrateContent(serialized);
    }

    /**
     * Public method to serialize content (for saving configurations)
     */
    public serializeForSave(content: any[]): any[] {
        return this.serializeElement(content);
    }

    /**
     * Load save from JSON string (used by Studio.loadSaveFromJson)
     */
    public loadSaveFromJson(jsonString: string): void {
        try {
            const parsed = JSON.parse(jsonString);
            const content = Array.isArray(parsed) ? parsed : [];
            const hydrated = this.hydrateContent(content);
            this.engine.content = hydrated;
        } catch (e) {
            console.error('Error loading save from JSON:', e);
        }
    }

    private serializeContent(content: any[]): any[] {
        return content.map((el) => this.serializeElement(el));
    }

    private serializeElement(element: any): any {
        const serialized = JSON.parse(
            JSON.stringify(element, (key, value) => {
                if (key === '_engine' || key === 'engine') {
                    return undefined;
                }
                if (key === '_handles' || key === 'handles' || key === 'selectedHandle') {
                    return undefined;
                }
                if (typeof value === 'function') {
                    return undefined;
                }
                // For images, capture the actual content (base64 or blob URL)
                if (key === '_content' && element?.type === 'Image') {
                    // Get the actual image content before serialization
                    try {
                        const content = element.content;
                        if (content && content.getContent) {
                            const actualContent = content.getContent();
                            return {
                                _mediaAddress: content._mediaAddress,
                                _actualContent: actualContent
                            };
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                }
                // For originalContent in images
                if (key === 'originalContent' && element?.type === 'Image') {
                    try {
                        const content = element.originalContent;
                        if (content && content.getContent) {
                            const actualContent = content.getContent();
                            return {
                                _mediaAddress: content._mediaAddress,
                                _actualContent: actualContent
                            };
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                }
                return value;
            })
        );

        // Also handle Image elements that are nested
        if (serialized?.type === 'Image' || serialized?.type === 'ImageSVG') {
            try {
                const content = element.content;
                if (content && content.getContent) {
                    serialized._content = {
                        _mediaAddress: content._mediaAddress,
                        _actualContent: content.getContent()
                    };
                }
                if (element.originalContent && element.originalContent.getContent) {
                    serialized.originalContent = {
                        _mediaAddress: element.originalContent._mediaAddress,
                        _actualContent: element.originalContent.getContent()
                    };
                }
            } catch (e) {
                // Ignore errors
            }
        }

        return serialized;
    }

    private hydrateContent(serialized: any[]): any[] {
        if (!Array.isArray(serialized)) {
            return [];
        }
        return serialized.map((data) => this.hydrateElement(data)).filter(Boolean);
    }

    private hydrateElement(data: any): any | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const type: string | undefined = data.type;
        const withEngine = { ...data, _engine: this.engine };

        // Restore image content to Media.engine.mediaList before hydrating
        if (type === 'Image' || type === 'ImageSVG') {
            try {
                // Handle _content restoration
                if (data._content && data._content._actualContent) {
                    const mediaAddress = data._content._mediaAddress;
                    const actualContent = data._content._actualContent;

                    // Ensure mediaList exists and has the content at the correct index
                    if (!this.engine.mediaList) {
                        this.engine.mediaList = [];
                    }

                    // Add content to mediaList at the specific index
                    while (this.engine.mediaList.length <= mediaAddress) {
                        this.engine.mediaList.push(null);
                    }
                    this.engine.mediaList[mediaAddress] = actualContent;

                    // Update _content to use the restored media address
                    (withEngine as any)._content = { _mediaAddress: mediaAddress };
                }

                // Handle originalContent restoration
                if (data.originalContent && data.originalContent._actualContent) {
                    const mediaAddress = data.originalContent._mediaAddress;
                    const actualContent = data.originalContent._actualContent;

                    if (!this.engine.mediaList) {
                        this.engine.mediaList = [];
                    }

                    while (this.engine.mediaList.length <= mediaAddress) {
                        this.engine.mediaList.push(null);
                    }
                    this.engine.mediaList[mediaAddress] = actualContent;

                    (withEngine as any).originalContent = { _mediaAddress: mediaAddress };
                }
            } catch (e) {
                console.error('Error restoring image content:', e);
            }
        }

        let hydrated: any | null = null;

        if (type === 'Theme' || type === 'theme') {
            const childData = Array.isArray(data._content) ? data._content : [];
            const hydratedChildren = childData.map((c: any) => this.hydrateElement(c)).filter(Boolean);
            (withEngine as any)._content = hydratedChildren;
            const theme = Theme.hydrate(withEngine);
            theme.engine = this.engine;
            hydrated = theme;
        } else if (type === 'BackPattern' || type === 'backPattern') {
            (withEngine as any)._content = this.hydrateElement(data._content);
            hydrated = BackPattern.hydrate(withEngine);
        } else {
            switch (type) {
                case 'Text':
                    hydrated = Text.hydrate(withEngine);
                    break;
                case 'Image':
                    hydrated = Image.hydrate(withEngine);
                    break;
                case 'ImageSVG':
                    hydrated = ImageSVG.hydrate(withEngine);
                    break;
                case 'Graduation':
                    hydrated = Graduation.hydrate(withEngine);
                    break;
                case 'CustomGraphicElement':
                    hydrated = CustomGraphicElement.hydrate(withEngine);
                    break;
                default:
                    hydrated = null;
            }
        }

        // Sidecars non geres par les `hydrate` des entites mais essentiels
        // au routage multi-zones (sinon, au load, tout finit dans la zone
        // active a la place de leur zone d'origine + la LayerBar n'arrive
        // pas a filtrer correctement par zone).
        if (hydrated) {
            if (data.zoneId !== undefined) {
                (hydrated as any).zoneId = data.zoneId;
            }
            if (data.canvasMeshId !== undefined) {
                (hydrated as any).canvasMeshId = data.canvasMeshId;
            }
            // Masque photo : _localClip (donnees plates serialisables) n'est pas
            // restaure par les hydrate() des entites => on le reporte ici, sinon
            // le masque disparait apres undo/redo ou rechargement.
            if (data._localClip !== undefined) {
                (hydrated as any)._localClip = data._localClip;
            }
        }

        return hydrated;
    }
}
